import axios from "axios";

const api = axios.create({
  baseURL: "https://accounting.lyon-jo.com/api",
});

// ========== Helpers ==========
const isOnLoginPage = () => window.location.pathname.startsWith("/login");
const isRefreshEndpoint = (url = "") => url.includes("/refresh");

const isPublicRequest = (config) => {
  if (config?.skipAuthRefresh) return true;
  const u = config?.url || "";
  return (
    u.includes("/accounting/login") ||
    u.includes("/accounting/password/forgot") ||
    u.includes("/accounting/password/verify-otp") ||
    u.includes("/accounting/password/reset") ||
    isRefreshEndpoint(u)
  );
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

// ========== Request interceptor ==========
api.interceptors.request.use(
  (config) => {
    if (!isPublicRequest(config)) {
      localStorage.setItem("lastActivity", Date.now());
    }

    if (isPublicRequest(config)) return config;
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ========== Response interceptor ==========
api.interceptors.response.use(
  (response) => {
    localStorage.setItem("lastActivity", Date.now());
    return response;
  },
  async (error) => {
    const originalRequest = error?.config || {};
    const status = error?.response?.status;
    const hasToken = !!localStorage.getItem("token");

    if (
      status === 401 &&
      !originalRequest._retry &&
      !isPublicRequest(originalRequest) &&
      !isOnLoginPage() &&
      hasToken &&
      !isRefreshEndpoint(originalRequest.url || "")
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = "Bearer " + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshRes = await axios.post(
          "https://accounting.lyon-jo.com/api/refresh",
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );

        const newToken = refreshRes?.data?.access_token;
        if (!newToken) throw new Error("No access token returned from refresh endpoint");
        localStorage.setItem("token", newToken);
        localStorage.setItem("lastActivity", Date.now());
        api.defaults.headers.common.Authorization = "Bearer " + newToken;
        processQueue(null, newToken);

        originalRequest.headers.Authorization = "Bearer " + newToken;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem("token");
        localStorage.removeItem("lastActivity");
        if (!isOnLoginPage()) window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// ========== Auto refresh timer (every 3 min) ==========
setInterval(async () => {
  const token = localStorage.getItem("token");
  if (!token || isOnLoginPage()) return;

  try {
    const response = await axios.post(
      "https://accounting.lyon-jo.com/api/refresh",
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const newToken = response?.data?.access_token;

    if (newToken) {
      localStorage.setItem("token", newToken);
      localStorage.setItem("lastActivity", Date.now());
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      console.log("Token refreshed successfully");
    } else {
      throw new Error("No token returned");
    }
  } catch (err) {
    console.error(" Auto-refresh failed:", err);
    localStorage.removeItem("token");
    localStorage.removeItem("lastActivity");

    if (!isOnLoginPage()) window.location.href = "/login";
  }
}, 3 * 60 * 1000);
